<template>
  <div>
    <div class="row">
      <div class="col-sm-12">
        <select
          v-model="recruiterId"
          :disabled="
            lockedToOfferRecruiter ||
            (activeRecruiter && activeRecruiter.manages_workers)
          "
          class="form-control"
          name="recruiter_id"
          required="true"
          data-test="recruiter-attribution"
        >
          <option value="">{{ $t('Select a recruiter') }}</option>
          <option
            v-for="recruiter in companyRecruitersFiltered"
            :key="recruiter.id"
            :value="recruiter.id"
            :data-test="`recruiter-attribution-recruiter-${recruiter.id}`"
          >
            {{ recruiter.company }}
          </option>
        </select>
        <input type="hidden" name="recruiter_id" :value="recruiterId" />
      </div>
    </div>

    <div v-if="recruiterId && recruiterId !== ''" class="m-t-10 row">
      <div v-if="!hideFee" v-show="!managesWorkers" class="col-sm-4">
        <label>{{ $t('Recruiter fee') }}</label>

        <div>
          <div v-if="changeFee">
            <InputPercentage
              v-model="feeInPercent"
              name="recruiter_fee"
              :decimal-point="decimalPoint"
              data-test-prefix="recruiter-fee-"
            />
          </div>
          <div v-else>
            <strong>{{ feeInPercent }} {{ $t('%') }}</strong>
          </div>

          <div class="clear"></div>
        </div>
      </div>

      <div class="col-sm-4">
        <label>{{ $t('Ownership period') }}</label>

        <div v-if="changeOwnership">
          <FeatureFlagged name="msp-mvp">
            <div class="row">
              <div class="col-sm-6">
                <input
                  v-model="ownershipDays"
                  type="number"
                  class="form-control"
                  name="ownership"
                  placeholder="Days"
                  :required="!neverExpires"
                  :disabled="neverExpires"
                  max="999"
                  :title="
                    $t(
                      'Enter the ownership period in number of days since the start date on a contract.',
                    )
                  "
                  data-test="recruiter-ownership-days"
                />
              </div>

              <div class="col-sm-6">
                <div class="m-t-10">{{ $t('Days') }}</div>
              </div>
            </div>
            <label class="m-t-10" style="display: flex; align-items: center; gap: 6px; cursor: pointer; margin-bottom: 0;">
              <input
                v-model="neverExpires"
                type="checkbox"
                data-test="recruiter-ownership-never-expires"
              />
              <span>{{ $t('Never expires') }}</span>
            </label>
            <template #is-disabled>
              <div class="row">
                <div class="col-sm-6">
                  <input
                    v-model="ownershipDays"
                    type="number"
                    class="form-control"
                    name="ownership"
                    placeholder="Days"
                    required="required"
                    max="999"
                    :title="
                      $t(
                        'Enter the ownership period in number of days since the start date on a contract.',
                      )
                    "
                    data-test="recruiter-ownership-days"
                  />
                </div>

                <div class="col-sm-6">
                  <div class="m-t-10">{{ $t('Days') }}</div>
                </div>
              </div>
            </template>
          </FeatureFlagged>
        </div>
        <div v-else>
          <strong v-if="ownershipDays === null">{{ $t('Never expires') }}</strong>
          <strong v-else>{{ ownershipDays }} {{ $t('days') }}</strong>
        </div>
      </div>

      <div class="col-sm-4">
        <label>{{ $t('Ownership start') }}</label>

        <PickerDatetime
          v-model="ownershipStartDate"
          name="ownership_start_date"
          :placeholder="$t('Start date')"
          format="YYYY-MM-DD"
        ></PickerDatetime>
      </div>
    </div>
  </div>
</template>

<script>
import moment from 'moment'
import PickerDatetime from '@/components/PickerDatetime.vue'
import InputPercentage from '@/components/InputPercentage.vue'
import FeatureFlagged from '@/components/FeatureFlagged.vue'

export default {
  components: {
    FeatureFlagged,
    PickerDatetime,
    InputPercentage,
  },
  props: {
    activeRecruiter: {
      type: Object,
      required: false,
      default: null,
    },

    companyRecruiters: {
      type: Array,
      required: true,
    },

    lockedToOfferRecruiter: {
      type: Boolean,
      default: false,
    },

    changeFee: {
      type: Boolean,
      required: true,
    },

    changeOwnership: {
      type: Boolean,
      required: true,
    },

    // The fee is managed by the parent (e.g. the attribution modal renders its
    // own basis-aware fee editor). Hides this component's percentage-only input.
    hideFee: {
      type: Boolean,
      default: false,
    },

    decimalPoint: {
      type: String,
      required: false,
      default: this?.account?.locale === 'da' ? ',' : '.',
    },
  },

  data() {
    return {
      recruiterId: '',
      recruiterFees: {},
      recruiterOwnershipDays: {},
      recruiterOwnershipStartDates: {},
      recruiterManagesWorkers: {},
      feeInPercent: 0,
      ownershipDays: null,
      ownershipStartDate: null,
      managesWorkers: false,
      neverExpires: false,
    }
  },

  computed: {
    // Always include the currently-attributed agency (it may not be in the
    // company's onboarded list on a supplier-mirror hire) and sort by name.
    allRecruiters() {
      const list = [...this.companyRecruiters]
      if (this.activeRecruiter && !list.some((recruiter) => recruiter.id === this.activeRecruiter.id)) {
        list.unshift(this.activeRecruiter)
      }
      return list.sort((a, b) => (a.company || '').localeCompare(b.company || ''))
    },
    companyRecruitersFiltered() {
      if (this.lockedToOfferRecruiter) {
        return this.allRecruiters.filter((SA) => SA.id === this.recruiterId)
      }
      return this.allRecruiters
    },
  },

  watch: {
    recruiterId(id) {
      this.$emit('update:recruiter-id', id)

      // Set the following values based on the selected recruiter
      this.managesWorkers = this.recruiterManagesWorkers[id]
      this.feeInPercent = this.transformPercentage(this.managesWorkers ? 0 : this.recruiterFees[id])
      this.ownershipDays = this.recruiterOwnershipDays[id]
      this.ownershipStartDate = this.recruiterOwnershipStartDates[id]
      this.neverExpires = this.recruiterOwnershipDays[id] === null
    },

    feeInPercent(value) {
      this.$emit('update:fee-in-percent', value)
    },

    ownershipDays(value) {
      this.$emit('update:ownership-days', value)
    },

    ownershipStartDate(value) {
      this.$emit('update:ownership-start-date', value)
    },

    neverExpires(value) {
      if (value) {
        this.ownershipDays = null
      }
      this.$emit('update:ownership-never-expires', value)
    },
  },

  created() {
    this.allRecruiters.forEach((recruiter) => {
      const isActiveRecruiter = this.activeRecruiter && recruiter.id === this.activeRecruiter.id

      this.recruiterFees[recruiter.id] = isActiveRecruiter
        ? this.activeRecruiter.fee
        : recruiter.fee

      this.recruiterOwnershipDays[recruiter.id] = isActiveRecruiter
        ? this.activeRecruiter.ownership_days
        : recruiter.ownership_days

      this.recruiterOwnershipStartDates[recruiter.id] = isActiveRecruiter
        ? this.activeRecruiter.ownership_start_date
        : moment().format('YYYY-MM-DD')

      this.recruiterManagesWorkers[recruiter.id] = isActiveRecruiter
        ? this.activeRecruiter.manages_workers
        : recruiter.manages_workers
    })

    if (this.activeRecruiter) {
      this.recruiterId = this.activeRecruiter.id
      this.managesWorkers = this.activeRecruiter.manages_workers
      this.feeInPercent = this.transformPercentage(
        this.managesWorkers ? 0 : this.activeRecruiter.fee,
      )
      this.ownershipDays = this.activeRecruiter.ownership_days
      this.ownershipStartDate = this.activeRecruiter.ownership_start_date
      this.neverExpires = this.activeRecruiter.ownership_days === null
    }
  },

  methods: {
    transformPercentage(value) {
      return value * 100
    },
  },
}
</script>
